import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  IconButton,
  Stack,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import Autocomplete from '@mui/material/Autocomplete';

const Report = () => {
  const [customRows, setCustomRows] = useState(() => {
    const savedSettings = localStorage.getItem('reportSettings');
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        return parsed.customRows || [{ id: 1, tags: [], amount: 0 }];
      } catch (e) {
        console.log('Error parsing saved report settings:', e);
        return [{ id: 1, tags: [], amount: 0 }];
      }
    }
    return [{ id: 1, tags: [], amount: 0 }];
  });
  
  const clearReportSettings = () => {
    localStorage.removeItem('reportSettings');
    setCustomRows([{ id: 1, tags: [], amount: 0 }]);
  };

  useEffect(() => {
    const settings = {
      customRows: customRows,
      lastUpdated: new Date().toISOString()
    };
    
    try {
      localStorage.setItem('reportSettings', JSON.stringify(settings));
    } catch (e) {
      console.log('Error saving report settings:', e);
    }
  }, [customRows]);

  const normalizeData = (data) => {
    if (!data) return data;
    return data.map(row => ({
      ...row,
      tags: (row.tags || []).map(tag => tag.toLowerCase().trim())
    }));
  };

  const [transactionData] = useState(() => {
    const savedData = localStorage.getItem('transactionData');
    return savedData ? normalizeData(JSON.parse(savedData)) : null;
  });
  
  // Calculate totals from the data
  const totals = useMemo(() => {
    if (!transactionData || transactionData.length === 0) return {
      taxTotal: 0,
      grandTotal: 0,
      tagTotals: {}
    };

    return transactionData.reduce((acc, row) => {
      const amount = parseFloat(row.amount) || 0;
      
      // Calculate tax total
      if (row.tax) {
        acc.taxTotal += amount;
      }

      // Calculate grand total
      acc.grandTotal += amount;

      // Calculate totals per tag
      (row.tags || []).forEach(tag => {
        acc.tagTotals[tag] = (acc.tagTotals[tag] || 0) + amount;
      });

      return acc;
    }, {
      taxTotal: 0,
      grandTotal: 0,
      tagTotals: {}
    });
  }, [transactionData]); // Add dependency on transactionData

  // Convert tag totals to grid rows
  const tagTotalRows = useMemo(() => {
    return Object.entries(totals.tagTotals).map(([tag, amount], index) => {
      const row = {
        id: index,
        tag: tag,
        amount: parseFloat(amount),
      }
      return row;
    });
  }, [totals.tagTotals]);

  const currencyFormatter = new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
  });

  // Grid columns for tag totals
  const tagTotalColumns = [
    { field: 'tag', headerName: 'Tag', flex: 1, sortable: true },
    { 
      field: 'amount', 
      headerName: 'Total Amount', 
      flex: 1, 
      sortable: true,
      type: 'number',
      valueFormatter: (params) => {
        return params != null ? currencyFormatter.format(params) : currencyFormatter.format(0);
      }
    }
  ];

  // Handle adding a new custom row
  const handleAddRow = () => {
    setCustomRows(prev => {
      const maxId = Math.max(...prev.map(row => row.id), 0);
      return [...prev, { id: maxId + 1, tags: [], amount: 0 }];
    });
  };

  // Handle removing a custom row
  const handleRemoveRow = (id) => {
    setCustomRows(prev => prev.filter(row => row.id !== id));
  };

  // Calculate totals for custom tag combinations
  const calculateCustomTotal = (rowTags) => {
    if (!transactionData || !rowTags || rowTags.length === 0) return 0;

    return transactionData.reduce((total, transaction) => {
      // Check if transaction has all the required tags
      const hasAllTags = rowTags.every(tag => 
        transaction.tags && transaction.tags.some(t => 
          t.toLowerCase().trim() === tag.toLowerCase().trim()
        )
      );

      if (hasAllTags) {
        total += parseFloat(transaction.amount) || 0;
      }

      return total;
    }, 0);
  };

  // Get all unique tags from the data
  const allTags = useMemo(() => {
    if (!transactionData) return [];
    const tagSet = new Set();
    transactionData.forEach(row => {
      (row.tags || []).forEach(tag => {
        tagSet.add(tag.toLowerCase().trim());
      });
    });
    return Array.from(tagSet);
  }, [transactionData]);

  // Handle tag changes in custom rows
  const handleTagChange = (rowId, newTags) => {
    setCustomRows(prev => prev.map(row => {
      if (row.id === rowId) {
        const amount = calculateCustomTotal(newTags);
        return { ...row, tags: newTags, amount };
      }
      return row;
    }));
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Financial Report
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Total Amount (All Items)
              </Typography>
              <Typography variant="h6">
                ${totals.grandTotal.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Total Amount (Tax Items)
              </Typography>
              <Typography variant="h6">
                ${totals.taxTotal.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Custom Tag Combinations */}
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Custom Tag Combinations
          </Typography>
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            onClick={handleAddRow}
          >
            Add Row
          </Button>
        </Box>
        
        {customRows.map((row) => (
          <Box 
            key={row.id} 
            sx={{ 
              display: 'flex', 
              gap: 2, 
              mb: 2, 
              alignItems: 'center',
            }}
          >
            <Autocomplete
              multiple
              options={allTags}
              value={row.tags}
              onChange={(event, newValue) => handleTagChange(row.id, newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Match all tags..."
                />
              )}
              fullWidth
            />
            <Typography sx={{ width: 150 }}>
              Total: {currencyFormatter.format(row.amount)}
            </Typography>
            <IconButton 
              onClick={() => handleRemoveRow(row.id)}
              color="error"
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        ))}
        <Box sx={{ display: 'flex', flexDirection: 'row-reverse' }}>
          <Button
            variant="outlined"
            onClick={clearReportSettings}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Delete all
          </Button>
        </Box>
      </Paper>

      {/* Tag Totals Grid */}
      <Paper sx={{ mt: 4, p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Total Amount per Tag
        </Typography>
        <Box>
          <DataGrid
            getRowHeight={() => 'auto'}
            rows={tagTotalRows}
            columns={tagTotalColumns}
            initialState={{
              sorting: {
                sortModel: [{ field: 'amount', sort: 'desc' }],
              },
            }}
            disableSelectionOnClick
            getRowId={(row) => row.id}
            components={{
              NoRowsOverlay: () => (
                <Stack height="100%" alignItems="center" justifyContent="center">
                  No tags found
                </Stack>
              ),
            }}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default Report;








